import { useEffect, useRef, type CSSProperties } from 'react';

import { SpeakerBadge } from '@/components/SpeakerBadge/SpeakerBadge';
import type { TimeSpan } from '@/papermill/extraction';
import {
  formatClock,
  speakerKey,
  wordAt,
  type SliceLine,
  type Speaker,
} from '@/papermill/transcript';

import styles from './TimeSlice.module.css';

export interface TimeSliceProps {
  span: TimeSpan;
  /** The span's segments and a turn either side, from `sliceOf`. */
  lines: SliceLine[];
  speakers: Speaker[];
  durationMs: number;
  /** Where playback is; words light up while this slice plays. */
  currentMs: number;
  /** True while this slice, rather than the whole recording, is playing. */
  playing: boolean;
  /** Plays just this moment; absent when the recording is unavailable. */
  onPlay?: () => void;
  onStop?: () => void;
}

type SliceWord = SliceLine['words'][number];

/** Consecutive words split by whether they are in the span, so a hit reads as one phrase. */
function runsOf(
  words: SliceWord[],
): { inSpan: boolean; words: { word: SliceWord; index: number }[] }[] {
  const runs: { inSpan: boolean; words: { word: SliceWord; index: number }[] }[] = [];
  words.forEach((word, index) => {
    const last = runs[runs.length - 1];
    if (last && last.inSpan === word.inSpan) {
      last.words.push({ word, index });
    } else {
      runs.push({ inSpan: word.inSpan, words: [{ word, index }] });
    }
  });
  return runs;
}

function pct(ms: number, durationMs: number): string {
  return `${Math.min(100, Math.max(0, (ms / Math.max(1, durationMs)) * 100))}%`;
}

/** The moment a value was said: its words in the turn around them, where it sits, and a replay. */
export function TimeSlice({
  span,
  lines,
  speakers,
  durationMs,
  currentMs,
  playing,
  onPlay,
  onStop,
}: TimeSliceProps) {
  const bySpeaker = new Map(speakers.map((speaker) => [speaker.key, speaker]));
  const speaker = span.speaker ? bySpeaker.get(speakerKey(span.track_id, span.speaker)) : undefined;
  const seconds = Math.max(0.1, (span.end_ms - span.start_ms) / 1000);
  const linesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = linesRef.current;
    const hit = box?.querySelector('mark');
    if (box && hit instanceof HTMLElement) {
      box.scrollTop = Math.max(0, hit.offsetTop - box.clientHeight / 3);
    }
  }, [span.start_ms, span.end_ms, span.track_id]);

  return (
    <div className={styles.slice}>
      <div className={styles.header}>
        {speaker ? (
          <SpeakerBadge speaker={speaker} showRole />
        ) : (
          <span className={styles.muted}>Said across speakers</span>
        )}
        <span className={styles.when}>
          {formatClock(span.start_ms, { tenths: true })}–
          {formatClock(span.end_ms, { tenths: true })}
          <span className={styles.muted}> · {seconds.toFixed(1)}s</span>
        </span>
      </div>

      <div className={styles.overview} aria-hidden="true">
        <span
          className={styles.window}
          style={{
            left: pct(span.start_ms, durationMs),
            width: `max(4px, ${pct(span.end_ms - span.start_ms, durationMs)})`,
          }}
        />
        {playing ? (
          <span className={styles.head} style={{ left: pct(currentMs, durationMs) }} />
        ) : null}
      </div>

      <div ref={linesRef} className={styles.lines}>
        {lines.length === 0 ? (
          <p className={styles.muted}>The transcript has no words at this moment.</p>
        ) : (
          lines.map((line) => {
            const who = bySpeaker.get(speakerKey(line.segment.track_id, line.segment.speaker));
            const active = playing && !line.context ? wordAt(line.words, currentMs) : -1;
            return (
              <div
                key={line.segment.id}
                className={`${styles.line} ${line.context ? styles.context : ''}`}
                style={{ '--speaker': `var(--series-${who?.slot ?? 1})` } as CSSProperties}
              >
                <span className={styles.lineMeta}>
                  {who?.name ?? 'Speaker'} ·{' '}
                  {formatClock(line.words[0]?.start_ms ?? line.segment.start_ms)}
                </span>
                <p className={styles.text}>
                  {line.clippedStart ? <span className={styles.more}>… </span> : null}
                  {runsOf(line.words).map((run) => {
                    const words = run.words.map(({ word, index }) => (
                      <span
                        key={index}
                        className={[
                          playing && word.inSpan && index <= active ? styles.lit : '',
                          playing && index === active && currentMs <= word.end_ms ? styles.now : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {word.text}
                      </span>
                    ));
                    const joined = words.flatMap((word, i) => (i === 0 ? [word] : [' ', word]));
                    return run.inSpan ? (
                      <span key={run.words[0].index}>
                        <mark className={styles.hit}>{joined}</mark>{' '}
                      </span>
                    ) : (
                      <span key={run.words[0].index}>{joined} </span>
                    );
                  })}
                  {line.clippedEnd ? <span className={styles.more}>…</span> : null}
                </p>
              </div>
            );
          })
        )}
      </div>

      {onPlay ? (
        <button
          type="button"
          className={`${styles.play} ${playing ? styles.playing : ''}`}
          onClick={playing ? onStop : onPlay}
        >
          <span className={styles.playIcon} aria-hidden="true">
            {playing ? '■' : '▶'}
          </span>
          {playing ? 'Stop' : 'Play this moment'}
        </button>
      ) : null}
    </div>
  );
}
